import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Loader, Check, AlertTriangle } from 'lucide-react';
import { reportApi, type Report } from '../../services/reportApi';
import { buildSkeletonDoc } from '../../utils/buildSkeletonDoc';
import { markdownToHtml } from '../../utils/markdownToProseMirror';
import { type SectionState } from './SectionManagerView';
import { type ReportPreferences } from './ConversationalSetupView';
import { SECTION_STYLE_GUIDANCE } from '../../utils/sectionGuidance';
import type { TemplateSection } from '../../services/templateApi';

type GenerationStatus = 'pending' | 'generating' | 'done' | 'failed' | 'skipped';
type OverallPhase = 'creating' | 'generating' | 'complete' | 'error';

// ─── Text cleanup helpers (mirrored from SectionManagerView) ──────────────
function stripPiiMarkers(text: string): string {
  return text.replace(/\{\{PII:[A-Z_]+\}\}/g, '').replace(/\{\{\/P(?:II|PI)(?::[A-Z_]+)?\}\}/g, '');
}

function stripMarkdownHeadings(text: string): string {
  return text.replace(/^#{1,6}\s+.*$/gm, (match) => {
    return match.replace(/^#{1,6}\s+/, '**') + '**';
  });
}

function stripAgentPreamble(text: string): string {
  let cleaned = text.replace(
    /^(?:(?:I (?:now |have |will |can ).*?(?:section|report|information).*?[.:])\s*(?:Here (?:it is|is the)[.:])?\s*\n*)+/i,
    '',
  );
  cleaned = cleaned.replace(/^Here (?:it is|is the [^:]+)[.:]\s*\n*/im, '');
  return cleaned.trim();
}

/**
 * Convert an HTML string into an array of ProseMirror-compatible JSON nodes.
 * Uses the DOM to parse HTML, then walks the tree to build JSON.
 */
function htmlToProseMirrorNodes(html: string): Record<string, unknown>[] {
  const wrapper = document.createElement('div');
  wrapper.innerHTML = html;
  const nodes: Record<string, unknown>[] = [];

  for (const child of Array.from(wrapper.children)) {
    const node = domElementToJson(child);
    if (node) {
      // Safety: never inject H2 headings into section body
      if (node.type === 'heading' && (node.attrs as any)?.level === 2) continue;
      nodes.push(node);
    }
  }

  return nodes.length > 0 ? nodes : [{ type: 'paragraph' }];
}

function domElementToJson(el: Element): Record<string, unknown> | null {
  const tag = el.tagName.toLowerCase();

  if (tag === 'p') {
    const content = inlineContent(el);
    return content.length > 0
      ? { type: 'paragraph', content }
      : { type: 'paragraph' };
  }

  if (/^h[1-6]$/.test(tag)) {
    const level = parseInt(tag[1], 10);
    const content = inlineContent(el);
    return content.length > 0
      ? { type: 'heading', attrs: { level }, content }
      : { type: 'heading', attrs: { level } };
  }

  if (tag === 'ul' || tag === 'ol') {
    const items: Record<string, unknown>[] = [];
    for (const li of Array.from(el.children)) {
      if (li.tagName.toLowerCase() === 'li') {
        const liContent: Record<string, unknown>[] = [];
        // Check for nested lists
        let hasNestedList = false;
        for (const liChild of Array.from(li.childNodes)) {
          if (liChild.nodeType === Node.ELEMENT_NODE) {
            const childEl = liChild as Element;
            const childTag = childEl.tagName.toLowerCase();
            if (childTag === 'ul' || childTag === 'ol') {
              hasNestedList = true;
              const nestedList = domElementToJson(childEl);
              if (nestedList) liContent.push(nestedList);
            } else if (childTag === 'p') {
              const pContent = inlineContent(childEl);
              liContent.push(pContent.length > 0 ? { type: 'paragraph', content: pContent } : { type: 'paragraph' });
            }
          }
        }
        if (!hasNestedList || liContent.length === 0) {
          // Treat entire li as a paragraph
          const pContent = inlineContent(li);
          if (pContent.length > 0) {
            liContent.unshift({ type: 'paragraph', content: pContent });
          } else if (liContent.length === 0) {
            liContent.push({ type: 'paragraph' });
          }
        }
        items.push({ type: 'listItem', content: liContent });
      }
    }
    return items.length > 0
      ? { type: tag === 'ul' ? 'bulletList' : 'orderedList', content: items }
      : null;
  }

  if (tag === 'blockquote') {
    const content: Record<string, unknown>[] = [];
    for (const child of Array.from(el.children)) {
      const node = domElementToJson(child);
      if (node) content.push(node);
    }
    return content.length > 0
      ? { type: 'blockquote', content }
      : { type: 'blockquote', content: [{ type: 'paragraph' }] };
  }

  if (tag === 'table') {
    // Simplified: convert table to paragraphs
    const rows: Record<string, unknown>[] = [];
    for (const tr of Array.from(el.querySelectorAll('tr'))) {
      const cells = Array.from(tr.querySelectorAll('td, th'));
      const text = cells.map(c => c.textContent?.trim() || '').join(' | ');
      if (text) rows.push({ type: 'paragraph', content: [{ type: 'text', text }] });
    }
    return rows.length > 0 ? rows[0] : null; // Return first row as fallback
  }

  // Fallback: treat as paragraph with text content
  const text = el.textContent?.trim();
  if (text) {
    return { type: 'paragraph', content: [{ type: 'text', text }] };
  }

  return null;
}

function inlineContent(el: Element | ChildNode): Record<string, unknown>[] {
  const result: Record<string, unknown>[] = [];

  for (const child of Array.from(el.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) {
      const text = child.textContent || '';
      if (text) result.push({ type: 'text', text });
    } else if (child.nodeType === Node.ELEMENT_NODE) {
      const childEl = child as Element;
      const tag = childEl.tagName.toLowerCase();
      const marks: Record<string, unknown>[] = [];

      if (tag === 'strong' || tag === 'b') marks.push({ type: 'bold' });
      if (tag === 'em' || tag === 'i') marks.push({ type: 'italic' });
      if (tag === 'u') marks.push({ type: 'underline' });
      if (tag === 'code') marks.push({ type: 'code' });
      if (tag === 'a') {
        marks.push({ type: 'link', attrs: { href: childEl.getAttribute('href') || '' } });
      }

      // Skip block elements inside inline context (e.g., nested ul/ol)
      if (['ul', 'ol', 'li', 'div', 'table', 'blockquote'].includes(tag)) continue;

      const innerNodes = inlineContent(childEl);
      for (const node of innerNodes) {
        if (marks.length > 0 && node.type === 'text') {
          const existingMarks = (node.marks as Record<string, unknown>[]) || [];
          result.push({ ...node, marks: [...existingMarks, ...marks] });
        } else {
          result.push(node);
        }
      }
    }
  }

  return result;
}

interface SectionProgress {
  id: string;
  title: string;
  status: GenerationStatus;
  charCount: number;
}

export interface GenerationProgressViewProps {
  caseId: string;
  templateId?: string;
  templateName: string;
  templateSections: TemplateSection[];
  userId: string;
  reportTitle: string;
  preferences: ReportPreferences;
  onComplete: (report: Report, sections: SectionState[]) => void;
  onError: (error: string) => void;
}

function buildSectionGuide(section: SectionState, prefs: ReportPreferences): string {
  const parts: string[] = [];

  if (section.placeholder) {
    parts.push(`Section purpose: ${section.placeholder}.`);
  }

  const styleGuide = SECTION_STYLE_GUIDANCE[section.title];
  if (styleGuide) {
    parts.push(styleGuide);
  }

  const prefParts: string[] = [];
  if (prefs.audience) prefParts.push(`Audience: ${prefs.audience}`);
  if (prefs.focusAreas.length > 0) prefParts.push(`Focus areas: ${prefs.focusAreas.join(', ')}`);
  if (prefs.keyEvents.length > 0) prefParts.push(`Key events to emphasise: ${prefs.keyEvents.join(', ')}`);
  if (prefs.scope) prefParts.push(`Scope: ${prefs.scope}`);
  if (prefs.citationStyle) prefParts.push(`Citation style: ${prefs.citationStyle}`);
  if (prefs.additionalGuidance) prefParts.push(prefs.additionalGuidance);

  if (prefParts.length > 0) {
    parts.push(`User preferences: ${prefParts.join('. ')}.`);
  }

  return parts.join('\n');
}

/**
 * Build the full ProseMirror document JSON from skeleton + accumulated section content.
 * Used for both incremental saves and final assembly.
 */
function assembleDocument(
  templateSections: TemplateSection[],
  reportTitle: string,
  contentMap: Map<string, string>,
): Record<string, unknown> {
  const skeletonDoc = buildSkeletonDoc(templateSections, reportTitle) as { type: string; content: Record<string, unknown>[] };
  const topNodes = skeletonDoc.content ?? [];
  const rebuiltContent: Record<string, unknown>[] = [];

  for (let i = 0; i < topNodes.length; i++) {
    const node = topNodes[i];
    rebuiltContent.push(node);

    if (
      (node as any).type === 'heading' &&
      (node as any).attrs?.level === 2 &&
      (node as any).content?.[0]?.text
    ) {
      const sectionTitle = (node as any).content[0].text;
      const rawContent = contentMap.get(sectionTitle);

      if (rawContent) {
        const cleaned = stripMarkdownHeadings(stripAgentPreamble(stripPiiMarkers(rawContent)));
        const html = markdownToHtml(cleaned);
        const contentNodes = htmlToProseMirrorNodes(html);
        rebuiltContent.push(...contentNodes);

        // Skip the empty paragraph that follows the H2 in the skeleton
        if (
          i + 1 < topNodes.length &&
          (topNodes[i + 1] as any).type === 'paragraph' &&
          !(topNodes[i + 1] as any).content
        ) {
          i++;
        }
      }
    }
  }

  return { type: 'doc', content: rebuiltContent };
}

// ─── Module-level generation runner ─────────────────────────────────────────
// Lives outside the component so it survives sidebar close/reopen (unmount/remount).
// The component subscribes to updates and renders the current state.

interface RunnerState {
  phase: OverallPhase;
  progress: SectionProgress[];
  errorMessage: string | null;
  report: Report | null;
  sectionContent: Map<string, string>;
  sections: SectionState[];
  reportTitle: string;
}

type RunnerListener = (state: RunnerState) => void;

interface ActiveRunner {
  /** Unique key to match runner to component instance (caseId + reportTitle) */
  key: string;
  state: RunnerState;
  aborted: boolean;
  listeners: Set<RunnerListener>;
  /** Callbacks to fire when generation completes — set by the component */
  onComplete: ((report: Report, sections: SectionState[]) => void) | null;
  onError: ((error: string) => void) | null;
}

let _activeRunner: ActiveRunner | null = null;

/** Check whether a generation runner is currently active for the given case + title. */
export function hasActiveRunner(caseId: string, reportTitle: string): boolean {
  return _activeRunner !== null
    && _activeRunner.key === `${caseId}::${reportTitle}`
    && !_activeRunner.aborted;
}

function getRunnerKey(caseId: string, reportTitle: string): string {
  return `${caseId}::${reportTitle}`;
}

function notifyListeners(runner: ActiveRunner) {
  for (const listener of runner.listeners) {
    listener({ ...runner.state });
  }
}

function updateRunnerProgress(runner: ActiveRunner, id: string, status: GenerationStatus, charCount?: number) {
  runner.state.progress = runner.state.progress.map(p =>
    p.id === id ? { ...p, status, charCount: charCount ?? p.charCount } : p,
  );
  notifyListeners(runner);
}

function persistRunnerState(runner: ActiveRunner, templateSections: TemplateSection[]) {
  const report = runner.state.report;
  if (!report || runner.state.sectionContent.size === 0) return;
  const doc = assembleDocument(templateSections, runner.state.reportTitle, runner.state.sectionContent);
  reportApi.updateReport(report.id, { content: JSON.stringify(doc) }).catch((err) => {
    console.warn('Incremental save failed:', err);
  });
}

async function generateSingleSection(
  runner: ActiveRunner,
  section: SectionState,
  reportId: string,
  allSections: SectionState[],
  caseId: string,
  preferences: ReportPreferences,
  templateSections: TemplateSection[],
): Promise<boolean> {
  if (runner.aborted) return false;
  updateRunnerProgress(runner, section.id, 'generating');

  const sectionGuide = buildSectionGuide(section, preferences);
  const otherSections = allSections
    .filter(s => s.id !== section.id)
    .map(s => s.title);

  const SECTION_TIMEOUT_MS = 120_000;

  return new Promise<boolean>((resolve) => {
    let accumulated = '';
    let resolved = false;
    const safeResolve = (value: boolean) => {
      if (resolved) return;
      resolved = true;
      clearTimeout(timeoutId);
      resolve(value);
    };

    const timeoutId = setTimeout(() => {
      console.warn(`Generation timed out for "${section.title}" after ${SECTION_TIMEOUT_MS / 1000}s`);
      if (accumulated.trim()) {
        runner.state.sectionContent.set(section.title, accumulated);
        updateRunnerProgress(runner, section.id, 'done', accumulated.length);
        runner.state.sections = runner.state.sections.map(s =>
          s.id === section.id ? { ...s, status: 'draft' as const } : s,
        );
        persistRunnerState(runner, templateSections);
        safeResolve(true);
      } else {
        updateRunnerProgress(runner, section.id, 'failed');
        safeResolve(false);
      }
    }, SECTION_TIMEOUT_MS);

    reportApi.generateSectionStream(
      reportId,
      {
        caseId,
        sectionTitle: section.title,
        sectionGuide: sectionGuide || undefined,
        otherSections,
      },
      (token: string) => {
        accumulated += token;
        updateRunnerProgress(runner, section.id, 'generating', accumulated.length);
      },
      () => {
        updateRunnerProgress(runner, section.id, 'done', accumulated.length);
        if (accumulated.trim()) {
          runner.state.sectionContent.set(section.title, accumulated);
        }
        runner.state.sections = runner.state.sections.map(s =>
          s.id === section.id ? { ...s, status: 'draft' as const } : s,
        );
        persistRunnerState(runner, templateSections);
        safeResolve(true);
      },
      (err: Error) => {
        console.error(`Generation failed for "${section.title}":`, err);
        updateRunnerProgress(runner, section.id, 'failed');
        safeResolve(false);
      },
    ).catch((err) => {
      console.error(`SSE connection failed for "${section.title}":`, err);
      updateRunnerProgress(runner, section.id, 'failed');
      safeResolve(false);
    });
  });
}

async function startGeneration(
  runner: ActiveRunner,
  props: {
    caseId: string;
    templateId?: string;
    templateSections: TemplateSection[];
    userId: string;
    reportTitle: string;
    preferences: ReportPreferences;
    aiSectionIds: Set<string>;
  },
) {
  const { caseId, templateId, templateSections, userId, reportTitle, preferences, aiSectionIds } = props;

  // Phase 1: Create report
  runner.state.phase = 'creating';
  notifyListeners(runner);

  let report: Report;
  try {
    const skeletonDoc = buildSkeletonDoc(templateSections, reportTitle);
    report = await reportApi.createReport({
      caseId,
      ...(templateId && !templateId.startsWith('__') ? { templateId } : {}),
      userId,
      title: reportTitle,
      status: 'generating',
      content: JSON.stringify(skeletonDoc),
    });
    runner.state.report = report;
  } catch (err: any) {
    const msg = err?.message || 'Failed to create report';
    console.error('Report creation failed:', err);
    runner.state.phase = 'error';
    runner.state.errorMessage = msg;
    notifyListeners(runner);
    runner.onError?.(msg);
    return;
  }

  // Phase 2: Generate all sections
  runner.state.phase = 'generating';
  notifyListeners(runner);
  const sectionStates = runner.state.sections;
  console.log(`[GenerationProgress] Starting generation of ${sectionStates.length} sections for report ${report.id}`);

  for (const section of sectionStates) {
    if (runner.aborted) break;

    if (!aiSectionIds.has(section.id)) {
      console.log(`[GenerationProgress] Skipping manual section: "${section.title}"`);
      continue;
    }

    console.log(`[GenerationProgress] Generating section: "${section.title}"`);
    const success = await generateSingleSection(runner, section, report.id, sectionStates, caseId, preferences, templateSections);
    console.log(`[GenerationProgress] Section "${section.title}" ${success ? 'completed' : 'failed'}`);
    await new Promise(r => setTimeout(r, 300));
  }

  if (runner.aborted) return;
  runner.state.phase = 'complete';
  notifyListeners(runner);

  // Final save with all content assembled
  try {
    const rebuiltDoc = assembleDocument(templateSections, reportTitle, runner.state.sectionContent);
    await reportApi.updateReport(report.id, { content: JSON.stringify(rebuiltDoc), status: 'complete' });
    const updatedReport = { ...report, content: JSON.stringify(rebuiltDoc), status: 'complete' as const };
    runner.onComplete?.(updatedReport, runner.state.sections);
  } catch (err) {
    console.error('Failed to assemble/save report content:', err);
    try {
      const fetched = await reportApi.getReport(report.id);
      runner.onComplete?.(fetched, runner.state.sections);
    } catch {
      runner.onComplete?.(report, runner.state.sections);
    }
  }

  // Runner is done — clear it so the next generation starts fresh
  if (_activeRunner === runner) {
    _activeRunner = null;
  }
}

// ─── Component ──────────────────────────────────────────────────────────────

export const GenerationProgressView: React.FC<GenerationProgressViewProps> = ({
  caseId,
  templateId,
  templateName,
  templateSections,
  userId,
  reportTitle,
  preferences,
  onComplete,
  onError,
}) => {
  const aiSectionIds = useMemo(
    () => new Set(templateSections.filter(s => s.aiGenerated !== false).map(s => s.id)),
    [templateSections],
  );

  // Initialise or reconnect to the module-level runner
  const runnerKey = getRunnerKey(caseId, reportTitle);

  const [snapshot, setSnapshot] = useState<RunnerState>(() => {
    // If there's an existing runner for this key, use its current state
    if (_activeRunner && _activeRunner.key === runnerKey) {
      return { ..._activeRunner.state };
    }
    // Otherwise, build initial state
    return {
      phase: 'creating',
      progress: templateSections.map(s => ({
        id: s.id,
        title: s.title,
        status: s.aiGenerated === false ? 'skipped' as GenerationStatus : 'pending' as GenerationStatus,
        charCount: 0,
      })),
      errorMessage: null,
      report: null,
      sectionContent: new Map(),
      sections: templateSections.map(s => ({
        id: s.id,
        title: s.title,
        placeholder: s.placeholder,
        status: 'empty' as const,
      })),
      reportTitle,
    };
  });

  useEffect(() => {
    // Check if there's already a runner for this key
    if (_activeRunner && _activeRunner.key === runnerKey) {
      // Reconnect: subscribe to updates and update callbacks
      const runner = _activeRunner;
      runner.onComplete = onComplete;
      runner.onError = onError;

      const listener: RunnerListener = (state) => setSnapshot({ ...state });
      runner.listeners.add(listener);

      // Sync current state immediately
      setSnapshot({ ...runner.state });

      return () => {
        runner.listeners.delete(listener);
      };
    }

    // No existing runner — create and start a new one
    const initialProgress = templateSections.map(s => ({
      id: s.id,
      title: s.title,
      status: s.aiGenerated === false ? 'skipped' as GenerationStatus : 'pending' as GenerationStatus,
      charCount: 0,
    }));

    const runner: ActiveRunner = {
      key: runnerKey,
      state: {
        phase: 'creating',
        progress: initialProgress,
        errorMessage: null,
        report: null,
        sectionContent: new Map(),
        sections: templateSections.map(s => ({
          id: s.id,
          title: s.title,
          placeholder: s.placeholder,
          status: 'empty' as const,
        })),
        reportTitle,
      },
      aborted: false,
      listeners: new Set(),
      onComplete,
      onError,
    };

    const listener: RunnerListener = (state) => setSnapshot({ ...state });
    runner.listeners.add(listener);

    _activeRunner = runner;

    startGeneration(runner, {
      caseId,
      templateId,
      templateSections,
      userId,
      reportTitle,
      preferences,
      aiSectionIds,
    });

    return () => {
      runner.listeners.delete(listener);
      // Do NOT abort on unmount — let generation continue in the background
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runnerKey]);

  // Keep callbacks up to date so completion fires the correct handler
  // (e.g. if the component remounts with fresh callback refs)
  useEffect(() => {
    if (_activeRunner && _activeRunner.key === runnerKey) {
      _activeRunner.onComplete = onComplete;
      _activeRunner.onError = onError;
    }
  }, [runnerKey, onComplete, onError]);

  const { phase, progress, errorMessage } = snapshot;

  const completedCount = progress.filter(p => p.status === 'done').length;
  const failedCount = progress.filter(p => p.status === 'failed').length;
  const skippedCount = progress.filter(p => p.status === 'skipped').length;
  const totalCount = progress.length - skippedCount;

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Header */}
      <div className="px-3 py-2.5 border-b border-medical-100 dark:border-medical-700 flex-shrink-0">
        <p className="text-sm font-semibold text-medical-800 dark:text-medical-200">
          {phase === 'creating' ? 'Creating Draft…' :
           phase === 'error' ? 'Draft Setup Failed' :
           'Generating Draft'}
        </p>
        <p className="text-[11px] text-medical-500 dark:text-medical-400 mt-0.5">
          {phase === 'creating'
            ? `Setting up "${reportTitle}"…`
            : phase === 'error'
              ? errorMessage
              : completedCount + failedCount < totalCount
                ? `Section ${completedCount + failedCount + 1} of ${totalCount}…`
                : failedCount > 0
                  ? `Done — ${completedCount} generated, ${failedCount} failed`
                  : 'All sections generated'}
        </p>

        {/* Progress bar */}
        {phase !== 'error' && (
          <div className="mt-2 h-1.5 bg-medical-100 dark:bg-medical-700 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ease-out ${
                phase === 'creating' ? 'bg-primary-400 dark:bg-primary-500 animate-pulse' : 'bg-primary-500 dark:bg-primary-400'
              }`}
              style={{
                width: phase === 'creating'
                  ? '10%'
                  : `${((completedCount + failedCount) / totalCount) * 100}%`,
              }}
            />
          </div>
        )}
      </div>

      {/* Creating phase spinner */}
      {phase === 'creating' && (
        <div className="flex-1 flex flex-col items-center justify-center gap-3 p-6">
          <Loader className="h-6 w-6 animate-spin text-primary-500" />
          <p className="text-xs text-medical-500 dark:text-medical-400">
            Creating report skeleton…
          </p>
        </div>
      )}

      {/* Error state */}
      {phase === 'error' && (
        <div className="flex-1 flex flex-col items-center justify-center gap-3 p-6">
          <AlertTriangle className="h-6 w-6 text-red-500" />
          <p className="text-xs text-red-600 dark:text-red-400 text-center">
            {errorMessage || 'Something went wrong. Please try again.'}
          </p>
        </div>
      )}

      {/* Section list (generating / complete phase) */}
      {(phase === 'generating' || phase === 'complete') && (
        <div className="flex-1 overflow-y-auto">
          <div className="px-3 py-2 space-y-0.5">
            {progress.map((p) => (
              <div
                key={p.id}
                className={`flex items-center gap-2 px-2 py-1.5 rounded-md transition-colors ${
                  p.status === 'generating' ? 'bg-amber-50 dark:bg-amber-900/10' : ''
                }`}
              >
                <div className="flex-shrink-0 w-4 h-4 flex items-center justify-center">
                  {p.status === 'pending' && (
                    <span className="w-1.5 h-1.5 rounded-full bg-medical-300 dark:bg-medical-600" />
                  )}
                  {p.status === 'generating' && (
                    <Loader className="h-3.5 w-3.5 animate-spin text-amber-500" />
                  )}
                  {p.status === 'done' && (
                    <Check className="h-3.5 w-3.5 text-emerald-500" />
                  )}
                  {p.status === 'failed' && (
                    <AlertTriangle className="h-3.5 w-3.5 text-red-500" />
                  )}
                  {p.status === 'skipped' && (
                    <span className="w-1.5 h-1.5 rounded-full bg-medical-200 dark:bg-medical-600" />
                  )}
                </div>

                <span className={`text-xs flex-1 min-w-0 truncate ${
                  p.status === 'generating'
                    ? 'text-amber-700 dark:text-amber-300 font-medium'
                    : p.status === 'done'
                      ? 'text-medical-600 dark:text-medical-400'
                      : p.status === 'failed'
                        ? 'text-red-600 dark:text-red-400'
                        : p.status === 'skipped'
                          ? 'text-medical-400 dark:text-medical-500 italic'
                          : 'text-medical-500 dark:text-medical-500'
                }`}>
                  {p.title}
                  {p.status === 'skipped' && (
                    <span className="text-[10px] not-italic ml-1">(manual)</span>
                  )}
                </span>

                {p.charCount > 0 && (
                  <span className="text-[10px] text-medical-400 dark:text-medical-500 flex-shrink-0">
                    {p.charCount.toLocaleString()} chars
                  </span>
                )}
              </div>
            ))}
          </div>

        </div>
      )}
    </div>
  );
};
